import {
  Archive,
  Download,
  FilterX,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  RotateCcw,
  Search,
  Table as TableIcon,
  User,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ActiveFilterChips, FiltersPopover } from '@/components/ui/list-filters';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { MemberSelect } from '@/features/members';
import { TagFilterSelect } from '@/features/tags';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { industryLabel, regionLabel } from '@/features/crm/companies/constants';
import { useIcpSegments } from '@/features/crm/icpSegments';
import { BASE as LEADS_BASE } from '../api';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { IcpFitBadge } from '../components/IcpFitBadge';
import { LeadQualificationFilters } from '../components/LeadQualificationFilters';
import {
  LEAD_STATUSES,
  sourceLabel,
  statusLabel,
  statusVariant,
} from '../constants';
import {
  NO_QUALIFICATION_FILTERS,
  qualificationFilterChips,
  qualificationFilterParams,
} from '../qualificationFilters';
import type { Lead } from '../types';
import { useArchiveLead, useLeads, usePurgeLead, useRestoreLead } from '../queries';
import { useLeadsUi, type LeadsViewMode } from '../store/viewPrefs';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type LeadOrderingColumn = 'title' | 'status' | 'source' | 'created' | 'modified';

const VIEW_MODES: readonly ViewModeOption<LeadsViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

export function LeadsListPage() {
  const navigate = useNavigate();
  const view = useLeadsUi((s) => s.view);
  const setView = useLeadsUi((s) => s.setView);
  const showArchived = useLeadsUi((s) => s.showArchived);
  const setShowArchived = useLeadsUi((s) => s.setShowArchived);
  const ordering = useLeadsUi((s) => s.ordering);
  const setOrdering = useLeadsUi((s) => s.setOrdering);
  const mine = useLeadsUi((s) => s.mine);
  const setMine = useLeadsUi((s) => s.setMine);
  // Persisted filters (survive reloads via the store).
  const status = useLeadsUi((s) => s.status);
  const setStatus = useLeadsUi((s) => s.setStatus);
  const owner = useLeadsUi((s) => s.owner);
  const setOwner = useLeadsUi((s) => s.setOwner);
  const tag = useLeadsUi((s) => s.tag);
  const setTag = useLeadsUi((s) => s.setTag);
  const createdFrom = useLeadsUi((s) => s.createdFrom);
  const setCreatedFrom = useLeadsUi((s) => s.setCreatedFrom);
  const createdTo = useLeadsUi((s) => s.createdTo);
  const setCreatedTo = useLeadsUi((s) => s.setCreatedTo);
  const qualification = useLeadsUi((s) => s.qualification);
  const setQualification = useLeadsUi((s) => s.setQualification);
  // Only the segment chip needs names; the Filters popover loads its own list.
  const segments = useIcpSegments(
    {},
    !['all', 'none'].includes(qualification.icpSegment),
  );

  // Search stays transient (cleared on reload).
  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  // Any non-default filter active → show the reset-all control.
  const onQualificationChange = (next: typeof qualification) => {
    setPage(1);
    setQualification(next);
  };
  const qualificationChips = qualificationFilterChips(
    qualification,
    onQualificationChange,
    (id) => segments.data?.results.find((s) => String(s.id) === id)?.name ?? 'Segment',
  );

  const filtersActive =
    searchInput !== '' ||
    status !== 'all' ||
    owner !== '' ||
    tag !== 'all' ||
    createdFrom !== '' ||
    createdTo !== '' ||
    mine ||
    showArchived ||
    qualificationChips.length > 0;

  function resetFilters() {
    setSearchInput('');
    setStatus('all');
    setOwner('');
    setTag('all');
    setCreatedFrom('');
    setCreatedTo('');
    setMine(false);
    setShowArchived(false);
    setQualification(NO_QUALIFICATION_FILTERS);
    setPage(1);
  }

  const { data, isPending, isError, error, isFetching } = useLeads({
    page,
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    archived: showArchived ? true : undefined,
    ordering,
    // Send `mine: true` only when active — omit the param entirely otherwise
    // so the backend treats it as a no-op (server reads the requesting user).
    mine: mine ? true : undefined,
    owner: owner ? Number(owner) : undefined,
    created_after: createdFrom || undefined,
    created_before: endOfDayParam(createdTo),
    tag: tag === 'all' ? undefined : Number(tag),
    ...qualificationFilterParams(qualification),
  });

  const archive = useArchiveLead();
  const restore = useRestoreLead();
  const purge = usePurgeLead();

  const results = data?.results ?? [];

  // The row actions vary per row depending on archived state — same callbacks,
  // wired once at the page level so the view renderers stay declarative.
  function handleArchive(lead: Lead) {
    archive.mutate(lead.id);
  }
  function handleRestore(lead: Lead) {
    restore.mutate(lead.id);
  }
  function handlePurge(lead: Lead) {
    purge.mutate(lead.id);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Leads</h1>
        <Button onClick={() => navigate('/crm/leads/new')}>
          <Plus /> New lead
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-44">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search leads…"
              className="h-9 pl-8"
              aria-label="Search leads"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => {
              setPage(1);
              setStatus(v);
            }}
          >
            {/* Content-width — sizes to the selected status, not a fixed box. */}
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <MemberSelect
            value={owner}
            onChange={(v) => {
              setPage(1);
              setOwner(v);
            }}
            placeholder="All sales"
            queryKey="leads-owner-filter"
            roles={CRM_OWNER_ROLES}
            clearable
            triggerClassName="h-9 w-auto min-w-28"
          />
          <TagFilterSelect
            value={tag}
            onChange={(v) => {
              setPage(1);
              setTag(v);
            }}
          />
          <FiltersPopover
            activeFilters={qualificationChips.length}
            onClear={() => onQualificationChange(NO_QUALIFICATION_FILTERS)}
            contentClassName="w-80"
          >
            <LeadQualificationFilters value={qualification} onChange={onQualificationChange} />
          </FiltersPopover>
          <DateRangeFilter
            label="Created"
            idPrefix="lead-created"
            from={createdFrom}
            to={createdTo}
            onFromChange={(v) => {
              setPage(1);
              setCreatedFrom(v);
            }}
            onToChange={(v) => {
              setPage(1);
              setCreatedTo(v);
            }}
          />
          {filtersActive ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={resetFilters}
              aria-label="Reset all filters"
              title="Reset all filters"
            >
              <FilterX className="size-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={mine ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setMine(!mine);
            }}
            aria-pressed={mine}
            aria-label={mine ? 'Showing my leads' : 'Show only my leads'}
          >
            <User className="size-4" /> Mine
          </Button>
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => {
              setPage(1);
              setShowArchived(!showArchived);
            }}
            aria-pressed={showArchived}
            aria-label={showArchived ? 'Showing archived leads' : 'Show archived leads'}
            title={showArchived ? 'Showing archived leads' : 'Show archived leads'}
          >
            <Archive className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // sends a `Content-Disposition: attachment` response so the
              // navigation downloads `leads.csv` without leaving the SPA.
              void exportCsv(LEADS_BASE, {
                search: search || undefined,
                status: status === 'all' ? undefined : status,
                archived: showArchived ? true : undefined,
                ordering,
                mine: mine ? true : undefined,
                owner: owner ? Number(owner) : undefined,
                created_after: createdFrom || undefined,
                created_before: endOfDayParam(createdTo),
                tag: tag === 'all' ? undefined : Number(tag),
                ...qualificationFilterParams(qualification),
                export: 'csv',
              });
            }}
            aria-label="Export leads as CSV"
            title="Export leads as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {qualificationChips.length ? (
        <ActiveFilterChips chips={qualificationChips} onResetAll={resetFilters} />
      ) : null}

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError).message}
        </p>
      ) : isPending ? (
        <LeadsSkeleton />
      ) : results.length === 0 ? (
        // Empty state is consistent across views: render the table chrome with
        // an inline "No leads found." row, so the page never collapses into a
        // tiny floating message regardless of the persisted view mode.
        <LeadsTable
          leads={[]}
          onRowClick={() => undefined}
          showArchived={showArchived}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onPurge={handlePurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <LeadsTable
          leads={results}
          onRowClick={(l) => navigate(`/crm/leads/${l.public_id}`)}
          showArchived={showArchived}
          onArchive={handleArchive}
          onRestore={handleRestore}
          onPurge={handlePurge}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <LeadsList leads={results} onOpen={(l) => navigate(`/crm/leads/${l.public_id}`)} />
      ) : view === 'cards' ? (
        <LeadsCards leads={results} onOpen={(l) => navigate(`/crm/leads/${l.public_id}`)} />
      ) : (
        <LeadsGroupedByStatus
          leads={results}
          onOpen={(l) => navigate(`/crm/leads/${l.public_id}`)}
        />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'lead' : 'leads'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  leads: Lead[];
  onOpen: (l: Lead) => void;
}

interface LeadsTableProps {
  leads: Lead[];
  onRowClick: (l: Lead) => void;
  showArchived: boolean;
  onArchive: (l: Lead) => void;
  onRestore: (l: Lead) => void;
  onPurge: (l: Lead) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function LeadsTable({
  leads,
  onRowClick,
  showArchived,
  onArchive,
  onRestore,
  onPurge,
  ordering,
  onOrderingChange,
}: LeadsTableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<LeadOrderingColumn>
              column="title"
              value={ordering}
              onChange={onOrderingChange}
            >
              Title
            </SortableTableHead>
            <SortableTableHead<LeadOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <SortableTableHead<LeadOrderingColumn>
              column="source"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden sm:table-cell"
            >
              Source
            </SortableTableHead>
            <TableHead className="hidden lg:table-cell">ICP fit</TableHead>
            <TableHead className="hidden xl:table-cell">Industry · Region</TableHead>
            <SortableTableHead<LeadOrderingColumn>
              column="created"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Created
            </SortableTableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-muted-foreground py-10 text-center">
                {showArchived ? 'No archived leads.' : 'No leads found.'}
              </TableCell>
            </TableRow>
          ) : (
            leads.map((l) => (
              <TableRow
                key={l.id}
                className="cursor-pointer"
                onClick={() => onRowClick(l)}
              >
                <TableCell className="font-medium">{l.title}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(l.status)}>{statusLabel(l.status)}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground hidden sm:table-cell">
                  {sourceLabel(l.source)}
                </TableCell>
                <TableCell className="hidden lg:table-cell">
                  <IcpFitBadge fit={l.icp_fit} segmentName={l.icp_segment_name} />
                </TableCell>
                <TableCell className="text-muted-foreground hidden xl:table-cell">
                  {companyProfileSummary(l)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {new Date(l.created).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  {l.is_archived ? (
                    <span className="inline-flex items-center justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Restore ${l.title}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore(l);
                        }}
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                      <PurgeArchivedButton name={l.title} onPurge={() => onPurge(l)} />
                    </span>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Archive ${l.title}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(l);
                      }}
                    >
                      <Archive className="size-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function LeadsCards({ leads, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {leads.map((l) => (
        <Card
          key={l.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(l)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(l);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{l.title}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={statusVariant(l.status)}>{statusLabel(l.status)}</Badge>
            <span className="text-muted-foreground">{sourceLabel(l.source)}</span>
            <span className="text-muted-foreground ml-auto">
              {new Date(l.created).toLocaleDateString()}
            </span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function LeadsList({ leads, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {leads.map((l) => (
        <li key={l.id}>
          {/* Use a real <button> so the row is interactive a11y-correctly. */}
          <button
            type="button"
            onClick={() => onOpen(l)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <Badge variant={statusVariant(l.status)} className="shrink-0">
              {statusLabel(l.status)}
            </Badge>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{l.title}</span>
            <span className="text-muted-foreground hidden text-xs sm:inline">
              {sourceLabel(l.source)}
            </span>
            <span className="text-muted-foreground text-xs">
              {new Date(l.created).toLocaleDateString()}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function LeadsGroupedByStatus({ leads, onOpen }: ListProps) {
  // Bucket by status, preserving the LEAD_STATUSES order (pipeline-ish).
  const byStatus = useMemo(() => {
    const map = new Map<string, Lead[]>();
    for (const status of LEAD_STATUSES) map.set(status.value, []);
    for (const lead of leads) {
      const list = map.get(lead.status) ?? [];
      list.push(lead);
      map.set(lead.status, list);
    }
    return map;
  }, [leads]);

  return (
    <div className="space-y-6">
      {LEAD_STATUSES.map(({ value, label }) => {
        const bucket = byStatus.get(value) ?? [];
        if (bucket.length === 0) return null;
        return (
          <section key={value} aria-labelledby={`status-${value}`}>
            <h2
              id={`status-${value}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <LeadsCards leads={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function LeadsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

/** "SaaS · Europe" from the linked company's profile; '—' when unknown. */
function companyProfileSummary(lead: Lead): string {
  const p = lead.company_profile;
  const parts = [
    p?.industry ? industryLabel(p.industry) : '',
    p?.region ? regionLabel(p.region) : '',
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}
